import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  WORKFLOW_CATALOGUE,
  type WorkflowInput,
  type WorkflowSubject,
  type WorkflowTestLine,
} from "@dentacore/shared-types";

import {
  createWorkflow,
  deleteWorkflow,
  getWorkflow,
  saveWorkflow,
  testWorkflow,
} from "../api/workflows";
import { useAuth } from "../auth/auth-context";
import { useModules } from "../shell/modules-context";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Board, type BoardHandle, type Selection } from "./board";
import { EdgePanel, NodePanel } from "./box-panel";
import { DetailsPanel } from "./details-panel";
import {
  addBox,
  clearSteps,
  emptyDraft,
  inputOf,
  needsTidy,
  removalCost,
  removeBox,
  sanitize,
  swapBoxes,
  tidy,
  updateEdge,
  updateNode,
  withAction,
  type NewBoxKind,
  type OpenEnd,
} from "./graph-model";
import { useWorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

type Confirm =
  | { kind: "leave"; to: string }
  | { kind: "delete" }
  | { kind: "subject"; subject: WorkflowSubject }
  | { kind: "remove"; id: string; cost: number }
  | null;

/**
 * **The workflow editor** (STORY-224, WF-D6–WF-D11, WG-D4): mostly canvas, the
 * details in a panel on the right that *Hide details* closes. Boxes drag
 * freely; adding, removing or reordering moves no placed box; **only *Tidy up*
 * and *Save* line them up**, and Save keeps the editor open to show it.
 * *Test run* asks the server what the draft would do today and changes
 * nothing. Leaving with unsaved changes asks first.
 *
 * Only an administrator builds or changes a workflow (WG-D14); the server
 * refuses anyone else whatever this draws.
 */
export function WorkflowEditorPage(): React.JSX.Element {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const modules = useModules();
  const on = modules.hasPreview("WORKFLOWS");
  const hasTasks = modules.hasPreview("TASKS");
  const admin = user?.canAdminister === true;
  const refs = useWorkflowRefs();

  const [id, setId] = useState<string | null>(workflowId ?? null);
  const [draft, setDraft] = useState<WorkflowInput | null>(() =>
    workflowId === undefined ? emptyDraft() : null,
  );
  /** The draft as last saved or loaded, to tell whether anything changed. */
  const [saved, setSaved] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  const [panel, setPanel] = useState(true);
  const [arranged, setArranged] = useState(false);
  const [test, setTest] = useState<WorkflowTestLine[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [scrollTarget, setScrollTarget] = useState<{ id: string; nonce: number } | null>(null);

  const board = useRef<BoardHandle | null>(null);
  const heights = useRef<ReadonlyMap<string, number>>(new Map());
  const tidiedOnOpen = useRef(false);
  /** The workflow already in hand — saving a new one moves the URL, which must not re-read it. */
  const loaded = useRef<string | null>(null);

  // Load the workflow being edited.
  useEffect(() => {
    if (workflowId === undefined || !on || loaded.current === workflowId) return;
    const controller = new AbortController();
    getWorkflow(workflowId, controller.signal)
      .then((view) => {
        const input = inputOf(view);
        loaded.current = view.id;
        setId(view.id);
        setDraft(input);
        setSaved(JSON.stringify(input));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not read the workflow");
      });
    return () => controller.abort();
  }, [workflowId, on]);

  const dirty = draft !== null && (id === null || JSON.stringify(draft) !== saved);

  // Closing the tab or reloading with unsaved changes asks the browser's question.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const change = (next: WorkflowInput): void => {
    setDraft(next);
    setTest(null);
    setNotice(null);
  };
  const changeGraph = (graph: WorkflowInput["graph"]): void => {
    if (draft !== null) change({ ...draft, graph });
  };

  /** Heights arrive after the first draw; a workflow made without the editor is tidied then. */
  const onMeasure = (measured: ReadonlyMap<string, number>): void => {
    heights.current = measured;
    if (tidiedOnOpen.current || draft === null) return;
    tidiedOnOpen.current = true;
    const fresh = id === null && draft.graph.nodes.length === 1;
    if (fresh || needsTidy(draft.graph)) {
      const lined = {
        ...draft,
        graph: tidy(draft.graph, measured, board.current?.viewportHeight() ?? 480),
      };
      setDraft(lined);
      // Placing boxes nobody placed is not an unsaved change.
      if (id !== null && saved === JSON.stringify(draft)) setSaved(JSON.stringify(lined));
    }
  };

  const lineUp = (input: WorkflowInput): WorkflowInput => ({
    ...input,
    graph: tidy(input.graph, heights.current, board.current?.viewportHeight() ?? 480),
  });

  const leave = (to: string): void => {
    if (dirty) setConfirm({ kind: "leave", to });
    else void navigate(to);
  };

  const save = async (): Promise<void> => {
    if (draft === null) return;
    if (draft.name.trim() === "") {
      setError("Give the workflow a name.");
      return;
    }
    // Saving lines the boxes up again, in the order the arrows show (WF-D8).
    const lined = lineUp({ ...draft, graph: sanitize(draft.graph) });
    setDraft(lined);
    setArranged(false);
    setBusy(true);
    setError(null);
    try {
      const view = id === null ? await createWorkflow(lined) : await saveWorkflow(id, lined);
      const input = inputOf(view);
      setDraft(input);
      setSaved(JSON.stringify(input));
      setNotice(`“${view.name}” saved.`);
      setTest(null);
      if (id === null) {
        loaded.current = view.id;
        setId(view.id);
        void navigate(`/workflows/${view.id}`, { replace: true });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the workflow");
    } finally {
      setBusy(false);
    }
  };

  const runTest = async (): Promise<void> => {
    if (draft === null) return;
    setTesting(true);
    setError(null);
    try {
      setTest(await testWorkflow(id, { ...draft, graph: sanitize(draft.graph) }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not test the workflow");
    } finally {
      setTesting(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (id === null) return;
    setBusy(true);
    try {
      await deleteWorkflow(id);
      setSaved(JSON.stringify(draft));
      void navigate("/workflows");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not delete the workflow");
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const add = (end: OpenEnd, kind: NewBoxKind): void => {
    if (draft === null) return;
    const made = addBox(draft.graph, draft.subject, end, kind, heights.current);
    changeGraph(made.graph);
    setSelection({ kind: "node", id: made.id });
    setPanel(true);
    setScrollTarget({ id: made.id, nonce: Date.now() });
  };

  const askRemove = (nodeId: string): void => {
    if (draft === null) return;
    const cost = removalCost(draft.graph, nodeId);
    if (cost > 0) setConfirm({ kind: "remove", id: nodeId, cost });
    else doRemove(nodeId);
  };
  const doRemove = (nodeId: string): void => {
    if (draft === null) return;
    changeGraph(removeBox(draft.graph, nodeId));
    setSelection(null);
    setConfirm(null);
  };

  const setSubject = (subject: WorkflowSubject, sure = false): void => {
    if (draft === null || subject === draft.subject) return;
    const hasSteps = draft.graph.nodes.length > 1;
    if (hasSteps && !sure) {
      setConfirm({ kind: "subject", subject });
      return;
    }
    const windows = WORKFLOW_CATALOGUE[subject].windows ?? [];
    const trigger =
      draft.trigger.kind === "event"
        ? { ...draft.trigger, field: null, value: null }
        : draft.trigger.kind === "schedule"
          ? { ...draft.trigger, window: windows[0]?.value ?? null }
          : draft.trigger;
    change({ ...draft, subject, trigger, graph: clearSteps(draft.graph) });
    setSelection(null);
    setConfirm(null);
  };

  const selectedNode = useMemo(
    () =>
      selection?.kind === "node"
        ? draft?.graph.nodes.find((n) => n.id === selection.id)
        : undefined,
    [selection, draft],
  );
  const selectedEdge = useMemo(
    () =>
      selection?.kind === "edge"
        ? draft?.graph.edges.find((e) => e.id === selection.id)
        : undefined,
    [selection, draft],
  );

  if (!on)
    return (
      <div className={styles.page} data-testid="workflows-editor">
        <p className={styles.empty}>Workflows is not switched on for this clinic.</p>
      </div>
    );
  if (!admin)
    return (
      <div className={styles.page} data-testid="workflows-editor">
        <p className={styles.empty}>
          Only an administrator builds or changes workflows.{" "}
          <Link to="/workflows">Back to Workflows</Link>
        </p>
      </div>
    );
  if (draft === null)
    return (
      <div className={styles.page} data-testid="workflows-editor">
        {error !== null ? (
          <p className={styles.error} role="alert" data-testid="wf-error">
            {error}
          </p>
        ) : (
          <p className={styles.empty}>Opening the workflow…</p>
        )}
      </div>
    );

  const ring = draft.trigger.kind === "ring";
  const problems = error?.split(" · ") ?? [];

  return (
    <div
      className={panel ? styles.editor : `${styles.editor} ${styles.editorWide}`}
      data-testid="workflows-editor"
    >
      <div className={styles.stage}>
        <div className={styles.bar}>
          <button
            type="button"
            className={styles.ghost}
            onClick={() => leave("/workflows")}
            data-testid="wf-back"
          >
            ← Workflows
          </button>
          <h1 className={styles.editorTitle}>
            {draft.name.trim() === "" ? "Untitled workflow" : draft.name}
          </h1>
          {dirty && (
            <span className={styles.sub} data-testid="wf-dirty">
              Not saved
            </span>
          )}
          {arranged && (
            <span className={styles.moved}>Moved by hand: Save or Tidy up lines it up</span>
          )}
          <span className={styles.grow} />
          <button
            type="button"
            className={styles.ghost}
            onClick={() => {
              change(lineUp(draft));
              setArranged(false);
            }}
            data-testid="wf-tidy"
          >
            Tidy up
          </button>
          <button
            type="button"
            className={styles.ghost}
            disabled={testing}
            onClick={() => void runTest()}
            data-testid="wf-test"
          >
            {testing ? "Testing…" : "Test run"}
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => void save()}
            data-testid="wf-save"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className={styles.ghost}
            aria-pressed={panel}
            onClick={() => setPanel(!panel)}
            data-testid="wf-panel-toggle"
          >
            {panel ? "Hide details ⟩" : "⟨ Details"}
          </button>
        </div>

        {error !== null && (
          <div className={styles.error} role="alert" data-testid="wf-error">
            {problems.length > 1 ? (
              <>
                <b>It cannot be saved yet:</b>
                <ul className={styles.problems}>
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </>
            ) : (
              error
            )}
            <button
              type="button"
              className={styles.x}
              aria-label="Dismiss"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}
        {notice !== null && (
          <p className={styles.notice} role="status" data-testid="wf-notice">
            {notice}
          </p>
        )}

        <Board
          graph={draft.graph}
          subject={draft.subject}
          trigger={draft.trigger}
          selection={selection}
          refs={refs}
          hasTasks={hasTasks}
          handle={board}
          scrollTarget={scrollTarget}
          onSelect={(next) => {
            // The Start box is the workflow itself: its details are the panel's own.
            const start = draft.graph.nodes.find((n) => n.kind === "start");
            setSelection(next?.kind === "node" && next.id === start?.id ? null : next);
            if (next !== null) setPanel(true);
          }}
          onMove={(nodeId, x, y) => {
            changeGraph(updateNode(draft.graph, nodeId, (n) => ({ ...n, x, y })));
            setArranged(true);
          }}
          onAdd={add}
          onSwap={(nodeId, direction) => changeGraph(swapBoxes(draft.graph, nodeId, direction))}
          onRemove={askRemove}
          onAction={(nodeId, action) =>
            changeGraph(
              updateNode(draft.graph, nodeId, (n) =>
                withAction(draft.graph, draft.subject, n, action),
              ),
            )
          }
          onMeasure={onMeasure}
        />

        {test !== null && (
          <section className={styles.testRun} data-testid="wf-test-result">
            <header className={styles.testHead}>
              <h2 className={styles.panelTitle}>Test run</h2>
              <span className={styles.sub}>
                {test.length} record{test.length === 1 ? "" : "s"} today. Nothing was changed.
              </span>
              <button
                type="button"
                className={styles.x}
                aria-label="Close the test run"
                onClick={() => setTest(null)}
              >
                ×
              </button>
            </header>
            {test.length === 0 ? (
              <p className={styles.sub}>Nothing it would run on today.</p>
            ) : (
              <ul className={styles.lines}>
                {test.map((line, index) => (
                  <li key={`${line.recordName}-${String(index)}`} data-testid="wf-test-line">
                    <b>{line.recordName}</b> <span className={styles.sub}>{line.outcome}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {panel && (
        <aside className={styles.inspector} aria-label="Workflow details" data-testid="wf-panel">
          {selectedNode !== undefined ? (
            <NodePanel
              graph={draft.graph}
              subject={draft.subject}
              node={selectedNode}
              ring={ring}
              hasTasks={hasTasks}
              refs={refs}
              onChange={(node) =>
                changeGraph(sanitize(updateNode(draft.graph, node.id, () => node)))
              }
              onRemove={askRemove}
              onClose={() => setSelection(null)}
            />
          ) : selectedEdge !== undefined ? (
            <EdgePanel
              graph={draft.graph}
              subject={draft.subject}
              edge={selectedEdge}
              refs={refs}
              onChange={(edge) => changeGraph(updateEdge(draft.graph, edge.id, () => edge))}
              onClose={() => setSelection(null)}
            />
          ) : (
            <DetailsPanel
              draft={draft}
              refs={refs}
              canDelete={id !== null}
              onChange={change}
              onSubject={(subject) => setSubject(subject)}
              onDelete={() => setConfirm({ kind: "delete" })}
            />
          )}
        </aside>
      )}

      {confirm?.kind === "leave" && (
        <ConfirmDialog
          title="Leave without saving?"
          confirmLabel="Leave"
          cancelLabel="Keep editing"
          tone="danger"
          onConfirm={() => {
            setSaved(JSON.stringify(draft));
            setConfirm(null);
            void navigate(confirm.to);
          }}
          onCancel={() => setConfirm(null)}
          testId="wf-confirm-leave"
        >
          Your changes to “{draft.name}” are not saved.
        </ConfirmDialog>
      )}
      {confirm?.kind === "delete" && (
        <ConfirmDialog
          title={`Delete “${draft.name}”?`}
          confirmLabel="Delete"
          cancelLabel="Keep it"
          tone="danger"
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirm(null)}
          testId="wf-confirm-delete"
        >
          It stops running, and its run log goes with it.
        </ConfirmDialog>
      )}
      {confirm?.kind === "subject" && (
        <ConfirmDialog
          title={`Run on ${WORKFLOW_CATALOGUE[confirm.subject].label} instead?`}
          confirmLabel="Change it and clear the steps"
          tone="danger"
          onConfirm={() => setSubject(confirm.subject, true)}
          onCancel={() => setConfirm(null)}
          testId="wf-confirm-subject"
        >
          Every step and condition here is about {WORKFLOW_CATALOGUE[draft.subject].label}, so they
          are cleared. Nothing is saved until you press Save.
        </ConfirmDialog>
      )}
      {confirm?.kind === "remove" && (
        <ConfirmDialog
          title="Remove this fork?"
          confirmLabel="Remove the fork"
          tone="danger"
          onConfirm={() => doRemove(confirm.id)}
          onCancel={() => setConfirm(null)}
          testId="wf-confirm-remove"
        >
          Its “otherwise” branch goes with it — {confirm.cost} box{confirm.cost === 1 ? "" : "es"}.
          The “if yes” branch joins the box before the fork.
        </ConfirmDialog>
      )}
    </div>
  );
}
